/// <amd-module name="Build/Admin/PoolsList/Stores/StoresHub" />
import { TaskAgent, TaskAgentQueue, TaskAgentPool } from "DistributedTask/Client/Contracts/TaskAgent";

import { AgentCloudStore } from "Build/Admin/Common/Stores/AgentCloudStore";
import { ConfirmationDialogStore } from "Build/Admin/Common/Stores/ConfirmationDialogStore";
import { NotificationStore } from "Build/Admin/Common/Stores/NotificationStore";
import { ProjectStore } from "Build/Admin/Common/Stores/ProjectStore";
import { ResourceLimitStore } from "Build/Admin/Common/Stores/ResourceLimitStore";
import { ServiceEndpointStore } from "Build/Admin/Common/Stores/ServiceEndpointStore";
import { VirtualMachineScaleSetStore } from "Build/Admin/Common/Stores/VirtualMachineScaleSetStore";
import { ActionsHub } from "Build/Admin/PoolsList/Actions/ActionsHub";
import { AgentPoolStatusStore } from "Build/Admin/PoolsList/Stores/AgentPoolStatusStore";
import { AgentPoolStore } from "Build/Admin/PoolsList/Stores/AgentPoolStore";
import { AgentQueueStore } from "Build/Admin/PoolsList/Stores/AgentQueueStore";
import { CreatePoolPanelStore } from "Build/Admin/PoolsList/Stores/CreatePoolPanelStore";
import { SecurityPanelStore } from "Build/Admin/PoolsList/Stores/SecurityPanelStore";
import { SecurityStore } from "Build/Admin/PoolsList/Stores/SecurityStore";

export class StoresHub {
    public agentCloudStore: AgentCloudStore;
    public agentPoolStore: AgentPoolStore;
    public agentPoolStatusStore: AgentPoolStatusStore;
    public agentQueueStore: AgentQueueStore;
    public confirmationDialogStore: ConfirmationDialogStore<TaskAgent | TaskAgentQueue | TaskAgentPool>;
    public createPoolPanelStore: CreatePoolPanelStore;
    public notificationStore: NotificationStore;
    public projectStore: ProjectStore;
    public resourceLimitStore: ResourceLimitStore;
    public securityStore: SecurityStore;
    public securityPanelStore: SecurityPanelStore;
    public serviceEndpointStore: ServiceEndpointStore;
    public virtualMachineScaleSetStore: VirtualMachineScaleSetStore;

    constructor(actionsHub: ActionsHub) {
        this.agentCloudStore = new AgentCloudStore();
        actionsHub.agentCloudActions.agentCloudsUpdated.addListener(this.agentCloudStore.updateAgentClouds);
        actionsHub.agentCloudActions.agentCloudAdded.addListener(this.agentCloudStore.addOrUpdateAgentCloud);
        actionsHub.agentCloudActions.agentCloudDeleted.addListener(this.agentCloudStore.deleteAgentCloud);

        this.agentPoolStore = new AgentPoolStore();
        actionsHub.agentPoolsUpdated.addListener(this.agentPoolStore.updateAgentPools);
        actionsHub.agentPoolAdded.addListener(this.agentPoolStore.addOrUpdateAgentPool);
        actionsHub.agentPoolDeleted.addListener(this.agentPoolStore.deleteAgentPool);

        this.agentPoolStatusStore = new AgentPoolStatusStore();
        actionsHub.agentPoolStatusUpdated.addListener(payload => this.agentPoolStatusStore.updateAgentPoolStatus(payload.poolId, payload.status));
        actionsHub.agentPoolStatusesUpdated.addListener(this.agentPoolStatusStore.updateAgentPoolStatuses);

        this.agentQueueStore = new AgentQueueStore();
        actionsHub.agentQueuesUpdated.addListener(this.agentQueueStore.updateAgentQueues);
        actionsHub.agentQueueDeleted.addListener(this.agentQueueStore.deleteAgentQueue);
        actionsHub.agentQueueAdded.addListener(this.agentQueueStore.addOrUpdateAgentQueue);

        this.confirmationDialogStore = new ConfirmationDialogStore<TaskAgent | TaskAgentQueue | TaskAgentPool>();
        actionsHub.confirmationDialogActions.showConfirmationDialog.addListener(this.confirmationDialogStore.showDialog);
        actionsHub.confirmationDialogActions.dismissConfirmationDialog.addListener(this.confirmationDialogStore.dismissDialog);

        this.createPoolPanelStore = new CreatePoolPanelStore();
        actionsHub.showCreatePoolPanel.addListener(this.createPoolPanelStore.showPanel);
        actionsHub.dismissCreatePoolPanel.addListener(this.createPoolPanelStore.dismissPanel);
        actionsHub.availablePoolTypesUpdated.addListener(this.createPoolPanelStore.availablePoolTypesUpdated);

        this.notificationStore = new NotificationStore();
        actionsHub.notificationActions.addNotification.addListener(this.notificationStore.addNotification);
        actionsHub.notificationActions.dismissNotification.addListener(this.notificationStore.dismissNotification);
        actionsHub.notificationActions.clearNotifications.addListener(this.notificationStore.clearNotifications);

        this.projectStore = new ProjectStore();
        actionsHub.projectActions.projectsUpdated.addListener(this.projectStore.updateProjects);

        this.resourceLimitStore = new ResourceLimitStore();
        actionsHub.resourceLimitActions.resourceLimitsUpdated.addListener(this.resourceLimitStore.resourceLimitsUpdated);
        actionsHub.resourceLimitActions.resourceLimitsStatusUpdated.addListener(this.resourceLimitStore.resourceLimitsStatusUpdated);

        this.securityStore = new SecurityStore();
        actionsHub.manageAllPermissionUpdated.addListener(this.securityStore.updateManageAllPermission);

        this.securityPanelStore = new SecurityPanelStore();
        actionsHub.showSecurityPanel.addListener(this.securityPanelStore.showPanel);
        actionsHub.dismissSecurityPanel.addListener(this.securityPanelStore.dismissPanel);

        this.serviceEndpointStore = new ServiceEndpointStore();
        actionsHub.serviceEndpointActions.azureSubscriptionsUpdated.addListener(this.serviceEndpointStore.updateAzureSubscriptions);
        actionsHub.serviceEndpointActions.azureSubscriptionsStatusUpdated.addListener(this.serviceEndpointStore.updateAzureSubscriptionsStatus);
        actionsHub.serviceEndpointActions.serviceEndpointsUpdated.addListener(payload => this.serviceEndpointStore.updateServiceEndpoints(payload.serviceEndpointScope, payload.serviceEndpoints));
        actionsHub.serviceEndpointActions.serviceEndpointsStatusUpdated.addListener(this.serviceEndpointStore.updateServiceEndpointStatus);
        actionsHub.serviceEndpointActions.serviceEndpointAuthorized.addListener(this.serviceEndpointStore.serviceEndpointAuthorized);

        this.virtualMachineScaleSetStore = new VirtualMachineScaleSetStore();
        actionsHub.serviceEndpointActions.virtualMachineScaleSetsUpdated.addListener(this.virtualMachineScaleSetStore.updateVirtualMachineScaleSets);
        actionsHub.serviceEndpointActions.virtualMachineScaleSetsErrorUpdated.addListener(this.virtualMachineScaleSetStore.updateVirtualMachineScaleSetsError);
        actionsHub.serviceEndpointActions.virtualMachineScaleSetsStatusUpdated.addListener(this.virtualMachineScaleSetStore.updateVirtualMachineScaleSetsStatus);
    }
}
